"use client"

import React, { useEffect, Suspense } from "react"
import { useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import Link from "next/link"
import { Eye, EyeOff, Gift, Loader2, Lock, Mail } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/lib/auth-context"
import { A9entLogo } from "@/components/a9ent-logo"
import { getPostAuthRoute } from "@/lib/auth-routing"
import {
  appendPromoToPath,
  getPendingPromotion,
  getPromoCodeFromSearchParams,
  resolvePromotionCode,
  savePendingPromotion,
} from "@/lib/promotion-storage"
import { usePublicAuthI18n } from "@/components/public-auth/i18n/public-auth-i18n-provider"
import { appendLngParam } from "@/lib/public-auth/append-lng-param"

const API_URL = process.env.NEXT_PUBLIC_API_URL_NEW || "http://localhost:3001"

export function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { login } = useAuth()
  const { t, locale } = usePublicAuthI18n()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [hasPendingPromo, setHasPendingPromo] = useState(false)

  useEffect(() => {
    const promoFromUrl = getPromoCodeFromSearchParams(searchParams)
    if (promoFromUrl) savePendingPromotion(promoFromUrl)
    setHasPendingPromo(Boolean(promoFromUrl || getPendingPromotion()))
  }, [searchParams])

  const getPostLoginDestination = (path: string) => {
    const promo = resolvePromotionCode(searchParams, { fallbackToPending: true })
    return appendPromoToPath(path, promo)
  }

  // Disabled for now: allow visiting /login while already authenticated
  // useEffect(() => {
  //   if (authLoading) return
  //   if (user) {
  //     const destination = getPostAuthRoute({
  //       role: user.role || user.profile?.role || null,
  //       onboardingComplete: user.onboardingComplete,
  //       profileSlug: user.profile?.profileSlug || null,
  //     })
  //     router.replace(getPostLoginDestination(destination))
  //   }
  // }, [authLoading, user, router, searchParams])

  useEffect(() => {
    const err = searchParams.get("error")
    if (err) setError(decodeURIComponent(err))
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    const result = await login(email, password)

    if (result.success) {
      let destination = "/admin"
      try {
        const token = localStorage.getItem("realtylink_token")
        if (token) {
          const res = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (res.ok) {
            const data = await res.json()
            destination = getPostAuthRoute({
              role: data?.user?.role || null,
              onboardingComplete: data?.user?.onboardingComplete,
              profileSlug: data?.user?.profileSlug || null,
            })
          }
        }
      } catch {
        // Fallback route will be /admin
      }
      router.push(getPostLoginDestination(destination))
    } else {
      setError(result.error || t("login.loginFailed"))
    }

    setIsLoading(false)
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-black text-white">
      <div className="absolute inset-0">
        <video
          className="h-full w-full object-cover opacity-45"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src="/landingpage/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/34 md:bg-black/45" />
      </div>

      <main className="relative z-10 flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="rounded-2xl border bg-card p-8 text-foreground shadow-sm">
            <div className="mb-6 text-center">
              <div className="mb-4 flex justify-center">
                <A9entLogo height={36} className="h-8 w-auto" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">{t("login.welcomeBack")}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{t("login.subtitle")}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {hasPendingPromo ? (
                <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground">
                  <Gift className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <p>{t("login.promoClaimCredits")}</p>
                </div>
              ) : null}
              {error && (
                <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive space-y-1">
                  <p>{error}</p>
                  {error.toLowerCase().includes("google") && (
                    <p className="text-muted-foreground">{t("login.googleHint")}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t("common.email")}</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("signup.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t("common.password")}</Label>
                  <Link
                    href={appendLngParam("/forgot-password", locale)}
                    className="text-xs text-muted-foreground hover:text-primary"
                  >
                    {t("login.forgotPassword")}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("login.passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="pl-9 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full bg-black text-white hover:bg-black/90" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("login.signingIn")}
                  </>
                ) : (
                  t("login.signInWithEmail")
                )}
              </Button>

              <div className="relative flex justify-center my-4">
                <span className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </span>
                <span className="relative bg-white px-2 flex w-fit justify-center text-xs uppercase text-muted-foreground dark:bg-card">
                  {t("common.orContinueWith")}
                </span>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full border-black/30 bg-white text-black hover:border-black/40 hover:bg-white/90 hover:text-black"
                disabled={isLoading}
                onClick={() => {
                  const promo = resolvePromotionCode(searchParams, { fallbackToPending: true })
                  if (promo) savePendingPromotion(promo)
                  const params = new URLSearchParams()
                  if (locale) params.set("lng", locale)
                  const query = params.toString()
                  window.location.href = `${API_URL}/api/auth/google${query ? `?${query}` : ""}`
                }}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                {t("login.signInWithGoogle")}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              {t("login.noAccount")}{" "}
              <Link href={appendLngParam("/signup", locale)} className="font-medium text-[#f39223] hover:underline">
                {t("login.signUpFree")}
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export function LoginFormSuspense() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  )
}
